import React, { useState, useEffect, useMemo } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Heading } from "../components/ui/Heading";
import { Button } from "../components/ui/Button";
import { QuantitySelector } from "../components/ui/QuantitySelector";
import { Price } from "../components/ui/Price";
import { Image } from "../components/ui/Image";
import { Spinner } from "../components/ui/Spinner";
import { useCart } from "../context/CartContext";
import { useCurrency } from "../context/CurrencyContext";
import { useApi } from "../context/ApiContext";
import { Text } from "../components/ui/Text";
import { FaExclamationTriangle, FaTrash } from "react-icons/fa";
import { getResizedImageUrl, ImagePresets } from "../utils/imageUtils";
import { useStoreLogoPlaceholder } from "../hooks/useStoreLogoPlaceholder";
import { findProductImage } from "../utils/productImageUtils";
export const Cart: React.FC = () => {
 const navigate = useNavigate();
 const { t } = useTranslation();
 const { cart, loading, updateItem, removeItem, clearCart } = useCart();
 const { currency } = useCurrency();
 const { api } = useApi();
 const storeLogoPlaceholder = useStoreLogoPlaceholder();
 const [estimatedShipping, setEstimatedShipping] = useState<number>(0);
 const [loadingShipping, setLoadingShipping] = useState(false);
 const [error, setError] = useState<string | null>(null);
 // Check if all products in cart are digital
 const isDigitalOnlyOrder = useMemo(() => {
  if (!cart || !cart.items || cart.items.length === 0) {
   return false;
  }
  return cart.items.every((item) => {
   const product = item.product as any;
   return product && product.isDigital === true;
  });
 }, [cart]);

 // Fetch estimated shipping cost
 useEffect(() => {
  const fetchEstimatedShipping = async () => {
   if (!cart || cart.items.length === 0) {
    setEstimatedShipping(0);
    return;
   }

   // Skip fetching shipping rates for digital-only orders
   if (isDigitalOnlyOrder) {
    setEstimatedShipping(0);
    return;
   }

   try {
    setLoadingShipping(true);
    
    if (!cart._id) {
     setEstimatedShipping(0);
     return;
    }
    
    // Send only cartId - backend will fetch all necessary data
    const response = await api.post("/shipping/rates", {
     cartId: cart._id,
     cartTotal: cart.subtotal || 0,
    });

    const rates = response.data.rates || [];
    // Use the first active shipping rate as the estimate
    if (rates.length > 0) {
     setEstimatedShipping(rates[0].price || 0);
    } else {
     setEstimatedShipping(0);
    }
    setError(null);
   } catch (error: any) {
    setError(error?.response?.data?.error || error?.message || "Failed to fetch estimated shipping");
   } finally {
    setLoadingShipping(false);
   }
  };

  fetchEstimatedShipping();
 }, [cart, api, isDigitalOnlyOrder]);

 const getEstimatedTotal = () => {
  if (!cart) return 0;
  return (cart.subtotal || 0) + (cart.tax || 0) + estimatedShipping;
 };

 if (loading) {
  return (
   <div className="w-full min-h-screen flex justify-center items-center">
    <Spinner size="lg" />
   </div>
  );
 }

 if (!cart || cart.items.length === 0) {
  return (
   <div className="w-full bg-white py-16">
    <div className="max-w-4xl mx-auto px-4 md:px-8 text-center">
     <Heading size="xl" className="mb-6 text-store-primary">
      {t("cart.empty")}
     </Heading>
     <Button
      onClick={() => navigate("/products")}
      className="bg-store-primary hover:bg-store-primary-dark text-white px-8 py-3"
     >
      {t("cart.continueShopping")}
     </Button>
    </div>
   </div>
  );
 }

 return (
  <div className="w-full bg-white py-8">
   <div className="max-w-7xl mx-auto px-4 md:px-8">
    {/* Breadcrumbs */}
    <div className="flex items-center gap-2 text-sm mb-6">
     <Link to="/" className="text-store-primary hover:text-store-secondary transition-colors">
      {t("breadcrumb.home")}
     </Link>
     <span className="text-store-accent">/</span>
     <span className="text-store-primary-light">{t("cart.title")}</span>
    </div>

    <Heading size="2xl" className="mb-8 text-store-primary">
     {t("cart.title")}
    </Heading>

    <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
     {/* Cart Items */}
     <div className="lg:col-span-2 space-y-4">
      {error && (
       <div className="bg-red-50 border border-red-200 text-red-700 p-4 mb-4 rounded-md">
        <Text size="sm" className="font-medium flex items-center">
         <FaExclamationTriangle className="text-red-500 mr-2" />
         {error}
        </Text>
       </div>
      )}
      {cart.items.map((item) => {
       const product = item.product as any;
       const selectedVariantOptions: Array<{ optionName: string; optionValue: string }> | undefined = item.selectedVariantOptions;
       const image = findProductImage(product, selectedVariantOptions);
       const imageUrl = image ? getResizedImageUrl(image.url, ImagePresets.cartItem, storeLogoPlaceholder) : storeLogoPlaceholder;
       const productUrl = `/products/${product?.handle || product?._id}`;
       return (
        <div
         key={item._id}
         className="bg-white border border-store-accent-light p-4 hover:border-store-secondary transition-all"
        >
         <div className="flex gap-4">
          <Link
           to={productUrl}
           className="w-24 h-24 bg-store-background overflow-hidden flex-shrink-0 cursor-pointer hover:opacity-80 transition-opacity"
          >
           <Image
            src={imageUrl}
            alt={product?.title || "Product"}
            aspectRatio="square"
            className="w-full h-full object-contain"
           />
          </Link>
          <div className="flex-1 min-w-0">
           <Link
            to={productUrl}
            className="hover:text-store-secondary transition-colors"
           >
            <Heading size="md" className="mb-1 text-store-primary">
             {product?.title}
            </Heading>
           </Link>
           {item.variant?.name && (
            <Text size="sm" color="muted" className="text-store-primary-light mb-2">
             {item.variant.name}
            </Text>
           )}
           {selectedVariantOptions && selectedVariantOptions.length > 0 && (
            <div className="mb-2 space-y-1">
             {selectedVariantOptions.map((option, idx) => (
              <Text key={idx} size="sm" color="muted" className="text-store-primary-light">
               {option.optionName}: {option.optionValue}
              </Text>
             ))}
            </div>
           )}
           {item.customization && (
            <Text size="sm" color="muted" className="text-store-primary-light mb-2">
             ✓ Customized
            </Text>
           )}
           <Price
            amount={item.price}
            currency={currency}
            size="md"
            className="text-store-accent font-bold"
           />
          </div>
          <div className="flex flex-col items-end gap-3">
           <div className="text-right">
            <div className="text-lg font-semibold text-store-primary">
             <Price
              amount={item.price * item.quantity}
              currency={currency}
             />
            </div>
           </div>
           {product?.isDigital ? (
            <div className="text-sm text-gray-600">
             {t("cart.quantity")}: 1
            </div>
           ) : (
            <QuantitySelector
             value={item.quantity}
             onChange={async (qty) => {
              // Prevent quantity changes for digital products
              if (product?.isDigital) {
               return;
              }
              try {
               await updateItem(item._id, qty);
              } catch (error: any) {
               const errorMessage = error?.response?.data?.error || error?.message || "Failed to update quantity";
               alert(errorMessage); // TODO: Replace with proper toast/notification system
               console.error("Error updating cart item:", error);
              }
             }}
             min={1}
            />
           )}
           <button
            onClick={() => removeItem(item._id)}
            className="text-red-600 hover:text-red-700 transition-colors flex items-center gap-1 text-sm"
           >
            <FaTrash className="text-xs" />
            {t("cart.remove")}
           </button>
          </div>
         </div>
        </div>
       );
      })}
     </div>

     {/* Order Summary */}
     <div className="lg:col-span-1">
      <div className="bg-store-background border border-store-accent-light p-6 sticky top-24">
       <Heading size="lg" className="mb-6 text-store-primary">
        {t("cart.orderSummary")}
       </Heading>
       <div className="space-y-3 mb-6">
        <div className="flex justify-between text-sm">
         <Text className="text-store-primary-light">{t("cart.subtotal")}</Text>
         <Price amount={cart.subtotal} currency={currency} className="text-store-primary" />
        </div>
        <div className="flex justify-between text-sm">
         <Text className="text-store-primary-light">{t("cart.tax")}</Text>
         <Price amount={cart.tax} currency={currency} className="text-store-primary" />
        </div>
        <div className="flex justify-between text-sm">
         <Text className="text-store-primary-light">
          {t("cart.shipping")}
          {loadingShipping && (
           <Text size="xs" className="text-gray-400 ml-1">
            (estimating...)
           </Text>
          )}
         </Text>
         {loadingShipping ? (
          <Spinner size="sm" />
         ) : (
          <Price
           amount={estimatedShipping}
           currency={currency}
           className="text-store-primary"
          />
         )}
        </div>
        <hr className="border-store-accent-light" />
        <div className="flex justify-between pt-2">
         <Text weight="semibold" size="lg" className="text-store-primary">
          {t("cart.total")}
         </Text>
         <Price
          amount={getEstimatedTotal()}
          currency={currency}
          size="lg"
          className="text-store-accent font-bold"
         />
        </div>
       </div>
       <Button
        disabled={!!error}
        onClick={() => {
         if (error) {
          return;
         }
         navigate("/checkout");
        }}
        className="w-full bg-store-primary hover:bg-store-primary-dark text-white py-3 mb-3"
       >
         {t("cart.proceedToCheckout")}
       </Button>
       <Button
        variant="outline"
        onClick={clearCart}
        className="w-full border-store-secondary text-store-primary hover:bg-store-background"
       >
        {t("cart.clearCart")}
       </Button>
      </div>
     </div>
    </div>
   </div>
  </div>
 );
};

export default Cart;
